'use client';

import { Stack, Typography, useTheme } from '@mui/material';
import mapboxgl from 'mapbox-gl';
import { layersConfig, nearbyPlacesSearchRadius } from '../../mapbox-common/config';
import { FsqPlace, Layer, MapType, PlaceProps, FsqPlacesSort } from '../../mapbox-common/types';
import { fetchFromFoursquare } from '../../mapbox-common/utils';
import { useMapboxStore, useMapboxStoreActions } from '../store';
import { LayerButton } from '../styles';

export default function LayersSwitch({
  t,
  mapType,
  lang = 'he',
  onLayersSwitch,
  foursquareKey,
}: {
  t: (key: string) => string;
  mapType: MapType;
  lang: string;
  onLayersSwitch?: (mapType: MapType, layer: Layer) => void;
  foursquareKey: string;
}) {
  const { shadows } = useTheme();

  const { mapRef, center, selectedLayers, savedLayers, displayLayers } = useMapboxStore();
  const { setSelectedLayer, addSavedLayer, setCurrentRoute, setCurrentPlaceId } =
    useMapboxStoreActions();

  const layerPlacesFormat = (layer: Layer, places: FsqPlace[]): PlaceProps[] => {
    const bounds = new mapboxgl.LngLatBounds();

    const newPlaces: PlaceProps[] = [];
    places.forEach((place) => {
      const newPlace = {
        fsq_id: place.fsq_id,
        lat: place.geocodes.main.latitude,
        lng: place.geocodes.main.longitude,
        name: place.name,
        address: place.location.formatted_address || '',
        // photo: place.photos?.[0]
        //   ? foursquarePhotoLink(place.photos[0].prefix, place.photos[0].suffix)
        //   : undefined,
        layer,
        routesLoaded: false,
      };
      newPlaces.push(newPlace);
      bounds.extend([newPlace.lng, newPlace.lat]);
    });
    mapRef?.fitBounds(bounds, { padding: 50, maxZoom: 15 });
    return newPlaces;
  };

  const handleLayerSwitch = async (layer: Layer) => {
    if (!mapRef || !center) return;

    setCurrentPlaceId(undefined);
    setCurrentRoute(undefined);

    onLayersSwitch && onLayersSwitch(mapType, layer);

    // save layer places if not saved yet
    if (!savedLayers.includes(layer)) {
      const places = await fetchFromFoursquare({
        url: 'https://api.foursquare.com/v3/places/search',
        p: {
          ll: `${center.location.lat},${center.location.lng}`,
          radius: nearbyPlacesSearchRadius,
          limit: '20',
          categories: layersConfig[layer].categories.join(','),
          sort: layersConfig[layer]?.sort || FsqPlacesSort.Popularity,
          fields: [
            'fsq_id',
            'name',
            'location',
            'link',
            'description',
            'geocodes',
            'photos',
            // chains,categories,stats,distance,email,fax,phone,verified
            // terms,attributes,related_places,timezone,hours,features,tips
          ].join(','),
        },
        foursquareKey,
        lang,
      });
      if (places.data.results.length) {
        addSavedLayer(layer, layerPlacesFormat(layer, places.data.results));
      } else {
        console.log('No places found for layer: ', layer);
      }
    }
    setSelectedLayer(layer);
  };

  return (
    <Stack
      spacing="10px"
      direction="row"
      sx={{
        width: '100%',
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        paddingBottom: '3px',
      }}
    >
      {Object.values(displayLayers).map((layer) => (
        <LayerButton
          key={layer}
          active={selectedLayers.includes(layer)}
          onClick={() => {
            handleLayerSwitch(layer);
          }}
          sx={{ boxShadow: shadows[1] }}
        >
          {layersConfig[layer as Layer].icon}
          <Typography>{t(`labels.${layer}`)}</Typography>
        </LayerButton>
      ))}
    </Stack>
  );
}
